// cesium-events.ts - Manages Cesium event handling

import {
  Cartesian3,
  Cesium3DTileColorBlendMode,
  Color,
  Entity,
  HeadingPitchRange,
  ScreenSpaceEventType,
} from "cesium";
import { setGisFeaturesOpacity } from "../../../utils/cesiumGisUtils";
import { tilesetRegistry } from "./cesium-assets";
import { flyToFeature } from "./cesium-camera";
import { changeImageryProvider } from "./cesium-imagery";
import {
  initializePolygonDrawing,
  onPolygonClick,
  onPolygonRightClick,
  startPolygonDrawing,
  stopPolygonDrawing,
} from "./cesium-polygon";
import { applyStylesToAllTilesets } from "./cesium-styling";

// State management for hover functionality
let currentHoveredEntity: Entity | null = null;
let hoverLabel: HTMLElement | null = null;
let originalCursor = "";

// State management for BIM feature highlighting
let selectedBimFeature: any = null;
const elementMap: { [key: number]: any[] } = {};
const batchIdMap: { [key: number]: any[] } = {};
let bimHighlightingEnabled = false;
let currentHighlightedTileFeature: any = null;

// Debug flag to control BIM highlighting logs
const BIM_HIGHLIGHTING_DEBUG = false;

// Create hover label element
function createHoverLabel(): HTMLElement {
  const label = document.createElement("div");
  label.id = "cesium-hover-label";
  label.style.cssText = `
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    font-family: 'Nunito', Arial, sans-serif;
    font-weight: 700;
    color: #333;
    pointer-events: none;
    z-index: 1000;
    max-width: 200px;
    word-wrap: break-word;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: none;
  `;

  // Create the triangle pointer
  const triangle = document.createElement("div");
  triangle.style.cssText = `
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid white;
    z-index: 1001;
  `;

  // Create the triangle border (for the border effect)
  const triangleBorder = document.createElement("div");
  triangleBorder.style.cssText = `
    position: absolute;
    left: -7px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-right: 7px solid #ccc;
    z-index: 1000;
  `;

  label.appendChild(triangleBorder);
  label.appendChild(triangle);
  document.body.appendChild(label);
  return label;
}

// Show hover label
function showHoverLabel(text: string, x: number, y: number) {
  if (!hoverLabel) {
    hoverLabel = createHoverLabel();
  }

  hoverLabel.textContent = text;

  // Position the label next to the cursor
  const offsetX = 15; // Distance to the right of the cursor
  const offsetY = -10; // Slightly above the cursor

  hoverLabel.style.left = `${x + offsetX}px`;
  hoverLabel.style.top = `${y + offsetY}px`;
  hoverLabel.style.display = "block";
}

// Hide hover label
function hideHoverLabel() {
  if (hoverLabel) {
    hoverLabel.style.display = "none";
  }
}

// Set cursor to pointer
function setPointerCursor(viewer: any) {
  if (viewer?.scene?.canvas) {
    originalCursor = viewer.scene.canvas.style.cursor;
    viewer.scene.canvas.style.cursor = "pointer";
  }
}

// Reset cursor to original
function resetCursor(viewer: any) {
  if (viewer?.scene?.canvas) {
    viewer.scene.canvas.style.cursor = originalCursor || "";
  }
}

// Get feature properties from picked object
function getFeatureProperties(pickedObject: any, viewer: any): any | null {
  if (!pickedObject) return null;

  try {
    // Handle GIS feature entities
    if (pickedObject.id instanceof Entity && pickedObject.id.properties) {
      return pickedObject.id.properties.getValue(viewer.clock.currentTime);
    }

    // Handle primitives associated with an entity (e.g., points)
    if (
      pickedObject.primitive?.id instanceof Entity &&
      pickedObject.primitive.id.properties
    ) {
      return pickedObject.primitive.id.properties.getValue(
        viewer.clock.currentTime,
      );
    }

    // Handle 3D tileset features - add more defensive checks
    const feature = pickedObject as any;

    // Check if this is a valid tileset feature
    if (!feature || typeof feature.getProperty !== "function") {
      return null;
    }

    const content = feature._content;

    // More defensive property table access
    if (
      content &&
      content.batchTable &&
      content.batchTable._propertyTable &&
      content.batchTable._propertyTable._metadataTable
    ) {
      const propertyTable = content.batchTable._propertyTable;
      const metadataTable = propertyTable._metadataTable;

      if (metadataTable && typeof metadataTable.getProperty === "function") {
        try {
          const propertyIds = metadataTable.getPropertyIds();
          const attributes: { [key: string]: any } = {};
          const batchId = feature._batchId;

          if (
            propertyIds &&
            Array.isArray(propertyIds) &&
            batchId !== undefined
          ) {
            propertyIds.forEach((id: string) => {
              try {
                const propertyValue = metadataTable.getProperty(batchId, id);
                if (propertyValue !== undefined) {
                  attributes[id] = propertyValue;
                }
              } catch (propError) {
                // Silently skip properties that can't be accessed
              }
            });
            return Object.keys(attributes).length > 0 ? attributes : null;
          }
        } catch (metadataError) {
          console.warn("Error accessing tileset metadata:", metadataError);
        }
      }
    }
  } catch (error) {
    console.warn("Error in getFeatureProperties:", error);
  }

  return null;
}

// Get correct screen coordinates accounting for canvas position
function getScreenCoordinates(viewer: any, canvasPosition: any) {
  if (!viewer?.scene?.canvas)
    return { x: canvasPosition.x, y: canvasPosition.y };

  const canvas = viewer.scene.canvas;
  const rect = canvas.getBoundingClientRect();

  return {
    x: rect.left + canvasPosition.x,
    y: rect.top + canvasPosition.y,
  };
}

// Setup hover event handlers
export function setupHoverHandlers(viewer: any, isMobileDevice = false) {
  if (!viewer?.screenSpaceEventHandler || isMobileDevice) {
    // Skip hover functionality on mobile devices
    return;
  }

  viewer.screenSpaceEventHandler.setInputAction((movement: any) => {
    try {
      const pickedObject = viewer.scene.pick(movement.endPosition);
      const properties = getFeatureProperties(pickedObject, viewer);

      // Handle BIM feature highlighting
      if (bimHighlightingEnabled) {
        // Per-feature hover highlight without altering saved styling
        if (
          currentHighlightedTileFeature &&
          currentHighlightedTileFeature !== pickedObject
        ) {
          try {
            const prev = (currentHighlightedTileFeature as any)._hoverPrevColor;
            if (prev) {
              currentHighlightedTileFeature.color = Color.clone(prev);
            } else {
              currentHighlightedTileFeature.color = Color.WHITE; // neutral in HIGHLIGHT mode
            }
            delete (currentHighlightedTileFeature as any)._hoverPrevColor;
          } catch {}
          currentHighlightedTileFeature = null;
        }

        if (
          pickedObject &&
          pickedObject.tileset &&
          typeof pickedObject.getProperty === "function"
        ) {
          try {
            if (!(pickedObject as any)._hoverPrevColor) {
              (pickedObject as any)._hoverPrevColor = Color.clone(
                pickedObject.color,
              );
            }
            pickedObject.color = Color.YELLOW;
            currentHighlightedTileFeature = pickedObject;
          } catch {}
        } else {
          if (currentHighlightedTileFeature) {
            try {
              const prev = (currentHighlightedTileFeature as any)
                ._hoverPrevColor;
              if (prev) {
                currentHighlightedTileFeature.color = Color.clone(prev);
              } else {
                currentHighlightedTileFeature.color = Color.WHITE;
              }
              delete (currentHighlightedTileFeature as any)._hoverPrevColor;
            } catch {}
            currentHighlightedTileFeature = null;
          }
        }
      }

      // Check if the picked object has an ideaId property
      const hasIdeaId = properties?.ideaId;

      if (hasIdeaId) {
        // Get the entity for comparison
        let hoveredEntity: Entity | null = null;
        if (pickedObject?.id instanceof Entity) {
          hoveredEntity = pickedObject.id;
        } else if (pickedObject?.primitive?.id instanceof Entity) {
          hoveredEntity = pickedObject.primitive.id;
        }

        // Get correct screen coordinates
        const screenCoords = getScreenCoordinates(viewer, movement.endPosition);

        // Only update if we're hovering over a different entity
        if (hoveredEntity !== currentHoveredEntity) {
          currentHoveredEntity = hoveredEntity;
          setPointerCursor(viewer);

          // Create label text - prioritize title, name, or ideaId
          const labelText =
            properties.title || properties.name || `Idea ${properties.ideaId}`;

          // Show label at mouse position
          showHoverLabel(labelText, screenCoords.x, screenCoords.y);
        } else if (hoverLabel) {
          // Update label position for same entity
          const offsetX = 15; // Match the offset
          const offsetY = -10;
          hoverLabel.style.left = `${screenCoords.x + offsetX}px`;
          hoverLabel.style.top = `${screenCoords.y + offsetY}px`;
        }
      } else {
        // Not hovering over a feature with ideaId, reset everything
        if (currentHoveredEntity) {
          currentHoveredEntity = null;
          resetCursor(viewer);
          hideHoverLabel();
        }
      }
    } catch (error) {
      // Silent fail for hover functionality
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);
}

// Cleanup hover functionality
export function cleanupHoverHandlers() {
  currentHoveredEntity = null;
  hideHoverLabel();
  if (hoverLabel) {
    document.body.removeChild(hoverLabel);
    hoverLabel = null;
  }
}

// BIM Feature Highlighting Functions

// Get element ID from feature
function getElement(feature: any): number | undefined {
  try {
    if (!feature || typeof feature.getProperty !== "function") {
      return undefined;
    }
    const elementValue = feature.getProperty("element");
    if (elementValue === undefined || elementValue === null) {
      return undefined;
    }
    const parsed = Number.parseInt(elementValue, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  } catch (error) {
    return undefined;
  }
}

// Set color for all features with the same element ID
function setElementColor(element: number, color: Color, viewer?: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  const featuresToColor = elementMap[element];
  if (!featuresToColor) {
    return;
  }

  const length = featuresToColor.length;

  for (let i = 0; i < length; ++i) {
    const feature = featuresToColor[i];
    // Don't change the color - just mark as highlighted
    feature._isHighlighted = true;
  }

  // Request a render to update the styles
  if (viewer && viewer.scene) {
    viewer.scene.requestRender();
  }
}

// Select a BIM feature (highlight in yellow)
function selectBimFeature(feature: any, viewer?: any) {
  if (!feature || !bimHighlightingEnabled) {
    console.log(
      "🔴 BIM highlighting: Cannot select feature - disabled or no feature",
    );
    return;
  }

  const element = getElement(feature);
  if (typeof element === "number") {
    setElementColor(element, Color.YELLOW, viewer);
    selectedBimFeature = feature;
  }
}

// Select a feature by batch ID (highlight in yellow)
function selectBimFeatureByBatchId(feature: any, viewer?: any) {
  if (!feature || !bimHighlightingEnabled) {
    console.log(
      "🔴 BIM highlighting: Cannot select feature by batch ID - disabled or no feature",
    );
    return;
  }

  const batchId = feature._batchId;
  console.log(
    "🟡 BIM highlighting: Selecting batch ID",
    batchId,
    "with",
    batchIdMap[batchId]?.length || 0,
    "features",
  );
  setBatchIdColor(batchId, Color.YELLOW, viewer);
  selectedBimFeature = feature;
}

// Set color for all features with the same batch ID
function setBatchIdColor(batchId: number, color: Color, viewer?: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  const featuresToColor = batchIdMap[batchId];
  if (!featuresToColor) {
    return;
  }

  const length = featuresToColor.length;

  for (let i = 0; i < length; ++i) {
    const feature = featuresToColor[i];
    // Don't change the color - just mark as highlighted
    feature._isHighlighted = true;
  }

  // Request a render to update the styles
  if (viewer && viewer.scene) {
    viewer.scene.requestRender();
  }
}

// Restore original color for all features with the same element ID
function restoreElementColor(element: number, viewer?: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  const featuresToColor = elementMap[element];
  if (!featuresToColor) {
    return;
  }

  const length = featuresToColor.length;

  for (let i = 0; i < length; ++i) {
    const feature = featuresToColor[i];
    // Remove highlighting flag - don't touch colors
    delete feature._isHighlighted;
  }

  // Request a render to update the styles
  if (viewer && viewer.scene) {
    viewer.scene.requestRender();
  }
}

// Restore original color for all features with the same batch ID
function restoreBatchIdColor(batchId: number, viewer?: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  const featuresToColor = batchIdMap[batchId];
  if (!featuresToColor) {
    return;
  }

  const length = featuresToColor.length;

  for (let i = 0; i < length; ++i) {
    const feature = featuresToColor[i];
    // Remove highlighting flag - don't touch colors
    delete feature._isHighlighted;
  }

  console.log(
    "✅ BIM highlighting: Highlighting removed for batch ID",
    batchId,
  );

  // Request a render to update the styles
  if (viewer && viewer.scene) {
    viewer.scene.requestRender();
  }
}

// Unselect a BIM feature (reset to original color)
function unselectBimFeature(feature: any, viewer?: any) {
  if (!feature) return;

  // Try element first, then batch ID
  const element = getElement(feature);
  if (typeof element === "number") {
    restoreElementColor(element, viewer);
  } else {
    const batchId = feature._batchId;
    if (batchId !== undefined) {
      restoreBatchIdColor(batchId, viewer);
    }
  }

  if (feature === selectedBimFeature) {
    selectedBimFeature = undefined;
  }
}

// Process content features for a tile
function processContentFeatures(
  content: any,
  callback: (feature: any) => void,
) {
  try {
    if (!content || typeof content.getFeature !== "function") {
      return;
    }

    const featuresLength = content.featuresLength;
    if (typeof featuresLength !== "number" || featuresLength <= 0) {
      return;
    }

    for (let i = 0; i < featuresLength; ++i) {
      try {
        const feature = content.getFeature(i);
        if (feature) {
          callback(feature);
        }
      } catch (featureError) {
        // Skip individual features that can't be processed
        console.warn(`Error processing feature ${i}:`, featureError);
      }
    }
  } catch (error) {
    console.warn("Error processing content features:", error);
  }
}

// Process all features in a tile
function processTileFeatures(tile: any, callback: (feature: any) => void) {
  try {
    if (!tile || !tile.content) {
      return;
    }

    const content = tile.content;
    const innerContents = content.innerContents;

    if (BIM_HIGHLIGHTING_DEBUG) {
      console.log(
        "🔧 BIM highlighting: Processing tile with",
        content.featuresLength || 0,
        "features",
      );
    }

    if (innerContents && Array.isArray(innerContents)) {
      const length = innerContents.length;
      for (let i = 0; i < length; ++i) {
        processContentFeatures(innerContents[i], callback);
      }
    } else {
      processContentFeatures(content, callback);
    }
  } catch (error) {
    console.warn("Error processing tile features:", error);
  }
}

// Load a BIM feature into the element map
function loadBimFeature(feature: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  try {
    // Try element first (for BIM models)
    const element = getElement(feature);
    if (typeof element === "number") {
      let features = elementMap[element];
      if (!features) {
        features = [];
        elementMap[element] = features;
      }
      features.push(feature);

      if (BIM_HIGHLIGHTING_DEBUG && elementMap[element].length === 1) {
        console.log("🔧 BIM highlighting: New element registered:", element);
      }
    }

    // Also try batch ID (for other 3D tile features)
    const batchId = feature._batchId;
    if (batchId !== undefined) {
      let features = batchIdMap[batchId];
      if (!features) {
        features = [];
        batchIdMap[batchId] = features;
      }
      features.push(feature);

      if (BIM_HIGHLIGHTING_DEBUG && batchIdMap[batchId].length === 1) {
        console.log("🔧 BIM highlighting: New batch ID registered:", batchId);
      }
    }

    if (BIM_HIGHLIGHTING_DEBUG) {
      console.log(
        "📊 BIM highlighting: Element map has",
        Object.keys(elementMap).length,
        "elements, Batch ID map has",
        Object.keys(batchIdMap).length,
        "batch IDs",
      );
    }
  } catch (error) {
    console.warn("Error loading BIM feature:", error);
  }
}

// Unload a BIM feature from the element map
function unloadBimFeature(feature: any) {
  // Only run if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  unselectBimFeature(feature);

  // Clean up from element map
  const element = getElement(feature);
  if (typeof element === "number") {
    const features = elementMap[element];
    if (features) {
      const index = features.indexOf(feature);
      if (index > -1) {
        features.splice(index, 1);
      }
    }
  }

  // Clean up from batch ID map
  const batchId = feature._batchId;
  if (batchId !== undefined) {
    const features = batchIdMap[batchId];
    if (features) {
      const index = features.indexOf(feature);
      if (index > -1) {
        features.splice(index, 1);
      }
    }
  }
}

// Setup BIM feature highlighting for a tileset
export function setupBimFeatureHighlighting(tileset: any) {
  if (!tileset) {
    console.warn("🔴 BIM highlighting: No tileset provided");
    return;
  }

  // Only set up event listeners if BIM highlighting is enabled
  if (!bimHighlightingEnabled) {
    return;
  }

  // Add tile load event listener
  tileset.tileLoad.addEventListener((tile: any) => {
    // Only process if highlighting is enabled
    if (bimHighlightingEnabled) {
      processTileFeatures(tile, loadBimFeature);
    }
  });

  // Add tile unload event listener
  tileset.tileUnload.addEventListener((tile: any) => {
    // Only process if highlighting is enabled
    if (bimHighlightingEnabled) {
      processTileFeatures(tile, unloadBimFeature);
    }
  });
}

// Enable/disable BIM feature highlighting
export function setBimHighlightingEnabled(enabled: boolean) {
  const wasEnabled = bimHighlightingEnabled;
  bimHighlightingEnabled = enabled;

  if (!enabled && selectedBimFeature) {
    unselectBimFeature(selectedBimFeature);
  }

  // If we're enabling highlighting and it wasn't enabled before, we need to set up
  // highlighting for any existing tilesets that were loaded while highlighting was disabled
  if (enabled && !wasEnabled) {
    // This will be handled by the caller if needed
    console.log(
      "🔧 BIM highlighting: Enabled - existing tilesets may need manual setup",
    );
  }
}

// Set up BIM highlighting for an existing tileset (call this when enabling highlighting after tilesets are loaded)
export function setupBimHighlightingForExistingTileset(tileset: any) {
  if (!tileset || !bimHighlightingEnabled) {
    return;
  }

  // Process existing loaded tiles
  if (tileset._tiles) {
    for (const tileId in tileset._tiles) {
      const tile = tileset._tiles[tileId];
      if (tile && tile.content) {
        processTileFeatures(tile, loadBimFeature);
      }
    }
  }
}

// Setup BIM feature hover highlighting (now integrated into main hover handler)
export function setupBimHoverHighlighting(viewer: any) {
  // This function is now deprecated - BIM highlighting is integrated into setupHoverHandlers
}

// Setup click event handlers
export function setupClickHandlers(
  viewer: any,
  onObjectClick?: (
    attributes: { [key: string]: any },
    cursorPosition?: { x: number; y: number },
  ) => void,
) {
  // Verify viewer exists
  if (!viewer || !viewer.screenSpaceEventHandler) {
    console.error("Cesium viewer not properly initialized!");
    return;
  }

  if (!onObjectClick) {
    // If no specific handler, still set up polygon interactions
    viewer.screenSpaceEventHandler.setInputAction((click: any) => {
      onPolygonClick(viewer, click);
    }, ScreenSpaceEventType.LEFT_CLICK);
    viewer.screenSpaceEventHandler.setInputAction(() => {
      onPolygonRightClick(viewer);
    }, ScreenSpaceEventType.RIGHT_CLICK);
    return;
  }

  viewer.screenSpaceEventHandler.setInputAction((click: any) => {
    try {
      // Get screen coordinates for cursor positioning
      const canvas = viewer.scene.canvas;
      const rect = canvas.getBoundingClientRect();
      const screenCoordinates = {
        x: rect.left + click.position.x,
        y: rect.top + click.position.y,
      };

      // First, handle polygon drawing clicks
      onPolygonClick(viewer, click);

      // Then, handle object picking
      const pickedObject = viewer.scene.pick(click.position);

      if (pickedObject) {
        const handleFeatureClick = (properties: any, entity: any) => {
          if (!properties) return;

          const clickedIdeaId = properties.ideaId;

          // Always update the visual opacity
          setGisFeaturesOpacity(viewer, clickedIdeaId);

          // Check if the clicked idea is already selected by looking at the current URL
          const currentUrl = window.location.pathname;
          const isIdeaAlreadySelected = currentUrl.includes(
            `/${clickedIdeaId}`,
          );

          // Prioritize navigation over URL opening
          // Only call onObjectClick if clicking a different idea than currently selected
          if (!isIdeaAlreadySelected) {
            console.log(
              "Calling onObjectClick with coordinates:",
              screenCoordinates,
            );
            onObjectClick(properties, screenCoordinates);
          } else {
            // If already selected, check for URL to open in new tab
            const url = properties.url || properties.link;
            if (url) {
              window.open(url);
            }
          }

          // flyToFeature(viewer, entity);
        };

        if (pickedObject.id instanceof Entity && pickedObject.id.properties) {
          // Handle clicks on GIS feature entities
          const properties = pickedObject.id.properties.getValue(
            viewer.clock.currentTime,
          );
          console.log("Clicked GIS Feature (Entity):", properties);
          handleFeatureClick(properties, pickedObject.id);
        } else if (
          pickedObject.primitive &&
          pickedObject.primitive.id instanceof Entity &&
          pickedObject.primitive.id.properties
        ) {
          // Handle clicks on primitives associated with an entity (e.g., points)
          const properties = pickedObject.primitive.id.properties.getValue(
            viewer.clock.currentTime,
          );
          console.log("Clicked GIS Feature (Primitive):", properties);
          handleFeatureClick(properties, pickedObject.primitive.id);
        } else {
          console.log(
            "Picked object is not a known GIS feature, treating as 3D tileset feature.",
          );
          // Handle clicks on 3D tileset features
          const feature = pickedObject as any;
          const content = feature._content;

          if (
            content &&
            content.batchTable &&
            content.batchTable._propertyTable
          ) {
            const propertyTable = content.batchTable._propertyTable;
            const metadataTable = propertyTable._metadataTable;

            if (
              metadataTable &&
              typeof metadataTable.getProperty === "function"
            ) {
              const attributes: { [key: string]: any } = {};
              try {
                const propertyIds = metadataTable.getPropertyIds();
                const batchId = feature._batchId;

                if (propertyIds && Array.isArray(propertyIds)) {
                  propertyIds.forEach((id: string) => {
                    try {
                      attributes[id] = metadataTable.getProperty(batchId, id);
                    } catch (propError) {
                      console.warn(`Failed to get property ${id}:`, propError);
                      // Continue with other properties
                    }
                  });
                  console.log(
                    "Calling onObjectClick with coordinates:",
                    screenCoordinates,
                  );

                  // Highlight the clicked BIM feature
                  if (bimHighlightingEnabled) {
                    // Clear previous selection first
                    if (selectedBimFeature) {
                      unselectBimFeature(selectedBimFeature, viewer);
                    }
                    selectBimFeature(feature, viewer);
                  }

                  onObjectClick(attributes, screenCoordinates);
                }
              } catch (metadataError) {
                console.error("Error extracting metadata:", metadataError);
                // Always dispatch event, even if metadata extraction failed
                const fallbackAttributes =
                  Object.keys(attributes).length > 0
                    ? attributes
                    : {
                        error: "Failed to extract metadata",
                        batchId: feature._batchId || "unknown",
                        featureType: "3D Tileset Feature",
                      };

                console.log(
                  "Calling onObjectClick with fallback attributes:",
                  screenCoordinates,
                );

                // Still highlight the feature even if metadata extraction failed
                if (bimHighlightingEnabled) {
                  // Clear previous selection first
                  if (selectedBimFeature) {
                    unselectBimFeature(selectedBimFeature, viewer);
                  }
                  selectBimFeature(feature, viewer);
                }

                onObjectClick(fallbackAttributes, screenCoordinates);
              }
            }
          }
        }
      } else {
        // Clicked on globe, clear selection but don't reset opacity
        // (opacity will persist until a new idea is selected)
        setGisFeaturesOpacity(viewer);

        // Also clear any BIM feature highlighting
        if (bimHighlightingEnabled && selectedBimFeature) {
          unselectBimFeature(selectedBimFeature, viewer);
        }

        // Dispatch custom event for empty clicks (clicking on map without hitting an object)
        // Also pass the cursor position for potential use
        window.dispatchEvent(
          new CustomEvent("cesiumEmptyClick", {
            detail: { cursorPosition: screenCoordinates },
          }),
        );
      }
    } catch (clickError) {
      console.error("Click handler error:", clickError);
      // Don't prevent event from being handled
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  viewer.screenSpaceEventHandler.setInputAction(() => {
    onPolygonRightClick(viewer);
  }, ScreenSpaceEventType.RIGHT_CLICK);
}

// Setup global event listeners
export function setupGlobalEvents(viewer: any, isMobileDevice: boolean) {
  window.addEventListener("depthTestChange", ((event: CustomEvent) => {
    if (viewer)
      viewer.scene.globe.depthTestAgainstTerrain = event.detail.enabled;
  }) as EventListener);

  window.addEventListener("shadowsChange", ((event: CustomEvent) => {
    if (viewer) viewer.scene.globe.enableLighting = false; // Keep shadows off
  }) as EventListener);

  window.addEventListener("styleConditionsChange", ((event: CustomEvent) => {
    if (viewer) {
      applyStylesToAllTilesets(
        viewer,
        tilesetRegistry,
        event.detail.conditions,
      );
    }
  }) as EventListener);

  window.addEventListener("imageryStyleChange", ((event: CustomEvent) => {
    if (viewer) {
      changeImageryProvider(
        viewer,
        event.detail.style,
        isMobileDevice,
        undefined,
        undefined,
      ).catch((error) => {});
    }
  }) as EventListener);

  window.addEventListener("polygonDrawingChange", ((event: CustomEvent) => {
    if (viewer) {
      if (event.detail.enabled) {
        initializePolygonDrawing(viewer);
        startPolygonDrawing(viewer);
      } else {
        stopPolygonDrawing(viewer);
      }
    }
  }) as EventListener);

  window.addEventListener("geoidHeightChange", ((event: CustomEvent) => {
    if (viewer) {
      // The geoid height change is already handled by setGlobalManualOffset in CesiumControls
      // This event listener is here for any additional viewer-specific updates that might be needed
      console.log("Geoid height changed to:", event.detail.height, "meters");
    }
  }) as EventListener);

  if (viewer && viewer.scene && viewer.scene.canvas) {
    viewer.scene.canvas.addEventListener(
      "webglcontextlost",
      (event: Event) => {
        event.preventDefault();
      },
      false,
    );
  }
}
